import React, { useEffect, useState } from 'react';
import { Box, Flex, FormControl, FormErrorMessage, Grid, Input, Text } from '@chakra-ui/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Handshake, Lock, PencilSimple, UploadSimple } from 'phosphor-react';
import { Controller, useFormContext } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useNfseEmissao } from '../../../../../../hooks/useNfseEmissao';
import { extractApiErrorMessage } from '../../../../../../utils/ErrorUtils';
import {
  INfseEmitente,
  INfseEmpresaBusca,
  INfseUltimaNotaResposta,
} from '../../../../../../types/nfseEmissao';
import { NFSE_PATHS } from '../../shared/constants';
import { EmpresaBusca } from '../../shared/EmpresaBusca';
import { island } from '../../shared/NfseIsland';
import { Banner, C, cnpjMask, dataBR, Eyebrow, MONO, NButton, StatusTag } from '../../shared/ui';
import { FormSection } from './FormSection';

interface EmitenteSectionProps {
  emitentes: INfseEmitente[];
  emitente?: INfseEmitente;
  ultimaNota?: INfseUltimaNotaResposta;
  numeroReservado?: number | null;
  serieReservada?: string;
  travado: boolean;
  podeAlterarNumeracao: boolean;
  onNumeracaoAlterada: () => void;
  // Empresa escolhida que ainda não emite: falta o certificado digital.
  empresaPendente: INfseEmpresaBusca | null;
  onEmpresaPendente: (empresa: INfseEmpresaBusca | null) => void;
  onEnviarCertificado: () => void;
}

const REGRA: Record<string, string> = {
  municipio_nao_aderente: 'RN14 · RN01',
  sem_ibge: 'RN14',
  sem_certificado: 'RN15',
  certificado_vencido: 'RN15',
  procuracao_vencida: 'RN15',
};

function titulo(e: INfseEmitente) {
  const doEscritorio = e.modo_certificado === 'procuracao';
  if (e.situacao === 'procuracao_vencida')
    return `A procuração NFS-e venceu em ${dataBR(e.procurador?.valida_ate)}`;
  if (doEscritorio && e.situacao === 'certificado_vencido')
    return `O certificado do escritório venceu em ${dataBR(e.certificado_valido_ate)}`;
  if (doEscritorio && e.situacao === 'sem_certificado')
    return 'O escritório procurador não tem certificado A1';
  if (e.situacao === 'municipio_nao_aderente')
    return `${e.municipio ?? 'O município'}/${e.uf ?? ''} não emite pela API Nacional`;
  if (e.situacao === 'sem_ibge') return `"${e.municipio ?? '—'}" não bateu com a tabela do IBGE`;
  if (e.situacao === 'certificado_vencido')
    return `O certificado venceu em ${dataBR(e.certificado_valido_ate)}`;
  return 'A empresa ainda não tem certificado A1';
}

// Bloqueio sempre diz o motivo e o que fazer a seguir (RN14 e RN15).
function Bloqueio({
  emitente,
  onEnviarCertificado,
}: {
  emitente: INfseEmitente;
  onEnviarCertificado: () => void;
}) {
  const navigate = useNavigate();
  if (emitente.situacao === 'liberado') return null;
  const certificado =
    emitente.situacao === 'sem_certificado' || emitente.situacao === 'certificado_vencido';
  // Com procuração, o acerto é na tela de certificados (data da procuração ou o
  // certificado do escritório), não no envio do .pfx da empresa.
  const naTelaDeCertificados =
    emitente.situacao === 'procuracao_vencida' ||
    (certificado && emitente.modo_certificado === 'procuracao');
  let acao = (
    <NButton size="sm" variant="secondary" onClick={() => navigate(NFSE_PATHS.empresas)}>
      Ver cadastro
    </NButton>
  );
  if (naTelaDeCertificados) {
    acao = (
      <NButton
        size="sm"
        variant="secondary"
        icon={Handshake}
        onClick={() => navigate(`${NFSE_PATHS.certificados}?emitente=${emitente.id}`)}
      >
        Ver procuração
      </NButton>
    );
  } else if (certificado) {
    acao = (
      <NButton size="sm" variant="secondary" icon={UploadSimple} onClick={onEnviarCertificado}>
        Enviar certificado
      </NButton>
    );
  }
  return (
    <Box mt={4}>
      <Banner tone="warning" action={acao}>
        <b>
          {REGRA[emitente.situacao]} · {titulo(emitente)}.
        </b>{' '}
        {emitente.motivo_bloqueio}
      </Banner>
    </Box>
  );
}

// Série e próxima DPS editadas no próprio card (TI-2026-0358). O backend grava o último
// nDPS como próximo - 1 e nunca repete número já usado aqui: se o informado for menor, a
// resposta traz o próximo que vale e a tela avisa.
function EditarNumeracao({
  emitenteId,
  serie,
  proximo,
  onSalvo,
  onFechar,
}: {
  emitenteId: number;
  serie: string;
  proximo?: number | null;
  onSalvo: () => void;
  onFechar: () => void;
}) {
  const { alterarNumeracao } = useNfseEmissao();
  const queryClient = useQueryClient();
  const [novaSerie, setNovaSerie] = useState(serie);
  const [novoProximo, setNovoProximo] = useState(proximo ? String(proximo) : '');
  const serieValida = /^\d{1,5}$/.test(novaSerie);
  const proximoValido = /^\d{1,15}$/.test(novoProximo) && Number(novoProximo) >= 1;

  const salvar = useMutation({
    mutationFn: () =>
      alterarNumeracao(emitenteId, {
        serie: novaSerie !== serie ? novaSerie : undefined,
        proximo_n_dps: Number(novoProximo),
      }),
    onSuccess: res => {
      queryClient.setQueryData(['nfse-ultima-nota', emitenteId], res);
      queryClient.invalidateQueries({ queryKey: ['nfse-emitentes'] });
      onSalvo();
      if (res.proximo_n_dps !== Number(novoProximo)) {
        island.notify(`A próxima DPS fica com o nº ${res.proximo_n_dps}`, {
          tone: 'warn',
          meta: `o nº ${novoProximo} já foi usado nesta série`,
          ms: 6000,
        });
      } else {
        island.notify('Numeração atualizada', {
          tone: 'ok',
          meta: `série ${res.serie} · próxima DPS nº ${res.proximo_n_dps}`,
        });
      }
      onFechar();
    },
    onError: err =>
      island.notify(extractApiErrorMessage(err, 'Não foi possível alterar a numeração'), {
        tone: 'bad',
      }),
  });

  const confirmar = () => {
    if (serieValida && proximoValido && !salvar.isPending) salvar.mutate();
  };
  const teclas = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      confirmar();
    }
    if (e.key === 'Escape') onFechar();
  };

  return (
    <Box mt={2}>
      <Grid templateColumns="64px minmax(0, 1fr)" gap={2}>
        <Box>
          <Text as="label" htmlFor="card-serie" fontSize="11px" color={C.subtle}>
            Série
          </Text>
          <Input
            id="card-serie"
            size="sm"
            inputMode="numeric"
            maxLength={5}
            value={novaSerie}
            isInvalid={!serieValida}
            onChange={e => {
              const nova = e.target.value.replace(/\D/g, '');
              setNovaSerie(nova);
              // Série nova recomeça do 1; o backend pula o que já foi usado nela.
              setNovoProximo(nova !== serie ? '1' : String(proximo ?? ''));
            }}
            onKeyDown={teclas}
          />
        </Box>
        <Box>
          <Text as="label" htmlFor="card-proximo" fontSize="11px" color={C.subtle}>
            Próxima DPS nº
          </Text>
          <Input
            id="card-proximo"
            size="sm"
            inputMode="numeric"
            maxLength={15}
            value={novoProximo}
            isInvalid={!proximoValido}
            autoFocus
            onChange={e => setNovoProximo(e.target.value.replace(/\D/g, ''))}
            onKeyDown={teclas}
          />
        </Box>
      </Grid>
      {novaSerie !== serie && serieValida && (
        <Text fontSize="11px" color={C.warning} mt={1}>
          Série nova recomeça a sequência da DPS.
        </Text>
      )}
      <Flex gap={2} mt={2}>
        <NButton
          size="sm"
          onClick={confirmar}
          isLoading={salvar.isPending}
          isDisabled={!serieValida || !proximoValido}
        >
          Salvar
        </NButton>
        <NButton size="sm" variant="ghost" onClick={onFechar} isDisabled={salvar.isPending}>
          Cancelar
        </NButton>
      </Flex>
    </Box>
  );
}

export function EmitenteSection({
  emitentes,
  emitente,
  ultimaNota,
  numeroReservado,
  serieReservada,
  travado,
  podeAlterarNumeracao,
  onNumeracaoAlterada,
  empresaPendente,
  onEmpresaPendente,
  onEnviarCertificado,
}: EmitenteSectionProps) {
  const {
    control,
    formState: { errors },
  } = useFormContext();

  const [editando, setEditando] = useState(false);
  const ultima = ultimaNota?.ultima_nota;
  const proximo = numeroReservado ?? ultimaNota?.proximo_n_dps ?? emitente?.proximo_n_dps;
  const serie = serieReservada ?? ultimaNota?.serie ?? emitente?.serie;
  const podeEditar = podeAlterarNumeracao && !travado;

  // A edição aberta é de uma empresa: trocar de emitente não pode levar os valores junto.
  useEffect(() => setEditando(false), [emitente?.id]);

  return (
    <FormSection
      n={1}
      title="Emitente"
      subtitle="Só emitem as empresas com município aderente e certificado válido"
    >
      <Grid
        templateColumns={{ base: '1fr', md: 'minmax(0, 1fr) 230px' }}
        gap={4}
        alignItems="stretch"
      >
        <Box minW={0}>
          <FormControl isRequired isInvalid={!!errors.emitente_id}>
            <Controller
              name="emitente_id"
              control={control}
              render={({ field }) => (
                <EmpresaBusca
                  inputId="emitente_id"
                  empresa={empresaPendente}
                  emitenteId={field.value}
                  emitentes={emitentes}
                  onSelecionar={escolhida => {
                    field.onChange(escolhida?.emitente_id ?? null);
                    onEmpresaPendente(escolhida && !escolhida.emitente_id ? escolhida : null);
                  }}
                  isDisabled={travado}
                  isInvalid={!!errors.emitente_id}
                />
              )}
            />
            {!empresaPendente && (
              <FormErrorMessage>{errors.emitente_id?.message as string}</FormErrorMessage>
            )}
          </FormControl>
          {emitente && (
            <Flex align="center" gap={2} mt={3} wrap="wrap">
              <StatusTag kind={emitente.situacao} />
              <Text fontSize="12.5px" color={C.subtle}>
                CNPJ {cnpjMask(emitente.cnpj)} · {emitente.municipio ?? '—'}/{emitente.uf ?? '—'}
                {emitente.codigo_ibge ? ` · IBGE ${emitente.codigo_ibge}` : ''}
              </Text>
              {emitente.modo_certificado === 'procuracao' && emitente.procurador && (
                <Text fontSize="12.5px" color={C.subtle}>
                  · Assina por procuração: <b>{emitente.procurador.razao_social}</b>
                </Text>
              )}
              {travado && (
                <Lock size={14} color={C.subtle} aria-label="Emitente travado nesta nota" />
              )}
            </Flex>
          )}
        </Box>
        <Box
          p="14px 16px"
          borderRadius="14px"
          bg={C.sunken}
          boxShadow={`inset 0 0 0 1px ${C.line}`}
        >
          <Eyebrow>Última nota emitida</Eyebrow>
          <Flex align="baseline" gap={2} mt={1.5}>
            <Text fontFamily={MONO} fontSize="22px" fontWeight={600} color={C.ink}>
              {ultima ? `Nº ${ultima.n_nfse ?? ultima.n_dps}` : '—'}
            </Text>
            <Text fontSize="12px" color={C.subtle}>
              {ultima && dataBR(ultima.data)}
              {ultima?.fonte === 'importada' && ' · emitida fora desta tela'}
              {!ultima && (emitente ? 'nenhuma ainda' : 'escolha a empresa')}
            </Text>
          </Flex>
          {ultima && (
            <Text fontSize="12px" color={C.subtle} mt={0.5} noOfLines={1}>
              DPS {ultima.serie}/{ultima.n_dps}
              {ultima.tomador_nome ? ` · ${ultima.tomador_nome}` : ''}
            </Text>
          )}
          {emitente && podeEditar && editando && serie && (
            <EditarNumeracao
              key={emitente.id}
              emitenteId={emitente.id}
              serie={serie}
              proximo={proximo}
              onSalvo={onNumeracaoAlterada}
              onFechar={() => setEditando(false)}
            />
          )}
          {emitente && !(podeEditar && editando) && proximo != null && (
            <Box mt={1}>
              <Text fontSize="12.5px" color={C.ink2}>
                Série {serie} · {numeroReservado ? 'reservado' : 'próxima DPS'}{' '}
                <Text as="b" color={C.ink}>
                  nº {proximo}
                </Text>
              </Text>
              {!numeroReservado && ultimaNota && !ultima && (
                <Text fontSize="12px" color={C.warning} mt={1}>
                  Se a empresa já emitia fora daqui, ajuste antes de emitir.
                </Text>
              )}
              {/* Número já reservado é desta nota: muda pelo "usar o próximo livre". */}
              {podeEditar && (
                <NButton
                  size="sm"
                  variant="secondary"
                  icon={PencilSimple}
                  mt={2}
                  onClick={() => setEditando(true)}
                >
                  Alterar série e número
                </NButton>
              )}
            </Box>
          )}
        </Box>
      </Grid>

      {empresaPendente && (
        <Box mt={4}>
          <Banner
            tone="warning"
            action={
              <NButton
                size="sm"
                variant="secondary"
                icon={UploadSimple}
                onClick={onEnviarCertificado}
              >
                Enviar certificado
              </NButton>
            }
          >
            <b>RN15 · A empresa ainda não tem certificado A1.</b> {empresaPendente.razao_social} só
            passa a emitir por esta tela depois que o certificado dela for enviado, ou com
            procuração NFS-e para o escritório (tela de certificados).
          </Banner>
        </Box>
      )}

      {emitente && <Bloqueio emitente={emitente} onEnviarCertificado={onEnviarCertificado} />}
    </FormSection>
  );
}
